import { LockOutlined } from '@ant-design/icons';
import { Card, Spin } from 'antd';
import type { ReactNode } from 'react';

import { useRbacStore } from './store';
import { useHasPermission } from './useHasPermission';

export type AuthenticationFailMode = 'disabler' | 'empty' | 'hide';

export interface AuthenticationProps {
  require: string | string[] | false;
  failMode: AuthenticationFailMode;
  children: ReactNode;
  emptyDescription?: string;
}

export function Authentication({
  require: requireKeys,
  failMode,
  children,
  emptyDescription = 'No permission to access',
}: AuthenticationProps) {
  const permissionsLoading = useRbacStore((s) => s.permissionsLoading);
  const hasPermission = useHasPermission(
    requireKeys === false ? [] : requireKeys,
  );

  if (!requireKeys) {
    return <>{children}</>;
  }

  if (permissionsLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          padding: 24,
        }}
      >
        <Spin tip="Loading permissions..." />
      </div>
    );
  }

  if (hasPermission) {
    return <>{children}</>;
  }
  switch (failMode) {
    case 'disabler':
      return (
        <div
          style={{
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
            display: 'inline-block',
          }}
        >
          <span style={{ pointerEvents: 'none' }}>{children}</span>
        </div>
      );
    case 'empty':
      return (
        <Card
          style={{ maxWidth: 420, margin: '24px auto' }}
          styles={{ body: { padding: 48, textAlign: 'center' } }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'var(--ant-color fillQuaternary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockOutlined
                style={{
                  fontSize: 32,
                  color: 'var(--ant-colorTextQuaternary)',
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: 'var(--ant-colorText)',
                  marginBottom: 4,
                }}
              >
                No permission
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: 'var(--ant-colorTextSecondary)',
                  lineHeight: 1.5,
                }}
              >
                {emptyDescription}
              </div>
            </div>
          </div>
        </Card>
      );
    case 'hide':
      return null;
    default:
      return null;
  }
}

export default Authentication;
